import { DOCUMENT, Injectable, inject, signal } from '@angular/core';

import { ThemeService } from './theme.service';

/**
 * Preferencias de accesibilidad del ciudadano (#278606, HU 202703 CA1 y CA3).
 *
 * Se aplican como clases y una variable CSS sobre `<html>` (ver `src/styles/accessibility.less`)
 * y se guardan en `localStorage` para que sobrevivan a la recarga y al cierre de sesión. El modo
 * oscuro reutiliza el tema `dark` que la aplicación ya compila como bundle diferido.
 *
 * El «lector de pantalla» usa la síntesis de voz del navegador: lee en voz alta el elemento que el
 * ciudadano enfoca o pulsa. No sustituye a un lector de pantalla del sistema (NVDA, VoiceOver); es
 * la ayuda que ofrecen widgets como UserWay y lo que pide el criterio 3.
 */
export interface AccessibilityState {
  /** Paso de tamaño de letra: índice en FONT_SCALES (0 = tamaño normal). */
  fontStep: number;
  darkMode: boolean;
  highContrast: boolean;
  grayscale: boolean;
  highlightLinks: boolean;
  bigCursor: boolean;
  readAloud: boolean;
}

export const FONT_SCALES = [0.9, 1, 1.1, 1.2, 1.35, 1.5];
export const DEFAULT_FONT_STEP = 1;

export const DEFAULT_ACCESSIBILITY_STATE: AccessibilityState = {
  fontStep: DEFAULT_FONT_STEP,
  darkMode: false,
  highContrast: false,
  grayscale: false,
  highlightLinks: false,
  bigCursor: false,
  readAloud: false,
};

const STORAGE_KEY = 'app.accessibility';
const CLASS_BY_FLAG: Record<keyof Omit<AccessibilityState, 'fontStep' | 'darkMode'>, string> = {
  highContrast: 'a11y-contrast',
  grayscale: 'a11y-grayscale',
  highlightLinks: 'a11y-links',
  bigCursor: 'a11y-cursor',
  readAloud: 'a11y-reading',
};
const READABLE_SELECTOR =
  'button, a, label, input, select, textarea, [role="button"], h1, h2, h3, h4, h5, h6, p, li, td, th';
const MAX_SPEECH_LENGTH = 400;

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private readonly document = inject(DOCUMENT);
  private readonly theme = inject(ThemeService);

  private readonly estado = signal<AccessibilityState>(this.leerPersistido());
  readonly state = this.estado.asReadonly();

  private readonly onRead = (event: Event) => this.speakTarget(event.target);
  private listening = false;

  constructor() {
    this.apply(this.estado());
  }

  get fontScale(): number {
    return FONT_SCALES[this.estado().fontStep] ?? 1;
  }

  get canIncreaseFont(): boolean {
    return this.estado().fontStep < FONT_SCALES.length - 1;
  }

  get canDecreaseFont(): boolean {
    return this.estado().fontStep > 0;
  }

  get isDefault(): boolean {
    const s = this.estado();
    return (Object.keys(DEFAULT_ACCESSIBILITY_STATE) as (keyof AccessibilityState)[]).every(
      k => s[k] === DEFAULT_ACCESSIBILITY_STATE[k]
    );
  }

  increaseFont(): void {
    if (this.canIncreaseFont) {
      this.update({ fontStep: this.estado().fontStep + 1 });
    }
  }

  decreaseFont(): void {
    if (this.canDecreaseFont) {
      this.update({ fontStep: this.estado().fontStep - 1 });
    }
  }

  toggle(flag: keyof Omit<AccessibilityState, 'fontStep'>): void {
    this.update({ [flag]: !this.estado()[flag] } as Partial<AccessibilityState>);
  }

  reset(): void {
    this.update({ ...DEFAULT_ACCESSIBILITY_STATE });
  }

  /** Lee en voz alta un texto arbitrario (lo usa el propio panel para anunciar sus acciones). */
  speak(text: string): void {
    const synth = this.document.defaultView?.speechSynthesis;
    const limpio = (text ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_SPEECH_LENGTH);
    if (!synth || !limpio) {
      return;
    }
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(limpio);
    utterance.lang = 'es-CO';
    synth.speak(utterance);
  }

  /** ¿El navegador ofrece síntesis de voz? Sin ella el botón de lectura se deshabilita. */
  get speechAvailable(): boolean {
    return Boolean(this.document.defaultView?.speechSynthesis);
  }

  private update(cambios: Partial<AccessibilityState>): void {
    const nuevo = { ...this.estado(), ...cambios };
    this.estado.set(nuevo);
    this.persistir(nuevo);
    this.apply(nuevo);
  }

  private apply(s: AccessibilityState): void {
    const root = this.document.documentElement;
    if (!root) {
      return;
    }
    root.style.setProperty('--a11y-zoom', String(FONT_SCALES[s.fontStep] ?? 1));
    (Object.keys(CLASS_BY_FLAG) as (keyof typeof CLASS_BY_FLAG)[]).forEach(flag => {
      root.classList.toggle(CLASS_BY_FLAG[flag], Boolean(s[flag]));
    });
    this.applyDarkMode(s.darkMode);
    this.applyReading(s.readAloud);
  }

  private applyDarkMode(dark: boolean): void {
    const actual = this.theme.currentTheme === 'dark';
    if (actual === dark) {
      return;
    }
    this.document.documentElement.setAttribute('data-kit-theme', dark ? 'dark' : 'default');
    this.theme.toggleTheme().catch(() => {
      // Sin el bundle del tema la app sigue usable con el tema actual.
    });
  }

  private applyReading(enabled: boolean): void {
    if (enabled === this.listening) {
      return;
    }
    this.listening = enabled;
    if (enabled) {
      this.document.addEventListener('focusin', this.onRead);
      this.document.addEventListener('click', this.onRead, true);
    } else {
      this.document.removeEventListener('focusin', this.onRead);
      this.document.removeEventListener('click', this.onRead, true);
      this.document.defaultView?.speechSynthesis?.cancel();
    }
  }

  private speakTarget(target: EventTarget | null): void {
    const el = target instanceof Element ? (target.closest(READABLE_SELECTOR) ?? target) : null;
    if (!el || el.closest('.a11y-widget')) {
      return;
    }
    this.speak(this.readableText(el));
  }

  /** Texto que se lee de un elemento: su etiqueta accesible, su rótulo y valor, o su texto visible. */
  readableText(el: Element): string {
    const aria = el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('alt');
    if (aria) {
      return aria;
    }
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
      const label = el.id
        ? this.document.querySelector(`label[for="${el.id}"]`)?.textContent
        : el.closest('label')?.textContent;
      const valor =
        el instanceof HTMLInputElement && ['checkbox', 'radio'].includes(el.type)
          ? el.checked
            ? 'marcado'
            : 'sin marcar'
          : el.value;
      return [label ?? el.getAttribute('placeholder') ?? '', valor].filter(Boolean).join('. ');
    }
    return (el as HTMLElement).innerText ?? el.textContent ?? '';
  }

  private leerPersistido(): AccessibilityState {
    try {
      const crudo = this.document.defaultView?.localStorage?.getItem(STORAGE_KEY);
      if (!crudo) {
        return { ...DEFAULT_ACCESSIBILITY_STATE };
      }
      const guardado = JSON.parse(crudo) as Partial<AccessibilityState>;
      const fontStep =
        Number.isInteger(guardado.fontStep) && FONT_SCALES[guardado.fontStep as number]
          ? (guardado.fontStep as number)
          : DEFAULT_FONT_STEP;
      return { ...DEFAULT_ACCESSIBILITY_STATE, ...guardado, fontStep };
    } catch {
      return { ...DEFAULT_ACCESSIBILITY_STATE };
    }
  }

  private persistir(s: AccessibilityState): void {
    try {
      this.document.defaultView?.localStorage?.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // Almacenamiento bloqueado: la preferencia vive solo en la sesión.
    }
  }
}
